<template>
  <div class="withdrawal-overview">
    <!-- 顶部筛选条件 -->
    <div class="filter-bar">
      <div class="filter-left">
        <span class="filter-label">时间范围：</span>
        <el-radio-group v-model="timeType" size="small" @change="onTimeTypeChange">
          <el-radio-button :label="0">今日</el-radio-button>
          <el-radio-button :label="1">昨日</el-radio-button>
          <el-radio-button :label="2">近7天</el-radio-button>
          <el-radio-button :label="3">近30天</el-radio-button>
          <el-radio-button :label="4">近半年</el-radio-button>
          <el-radio-button :label="5">近一年</el-radio-button>
        </el-radio-group>
      </div>
      <div class="filter-right">
        <el-button type="primary" size="small" icon="el-icon-refresh" @click="refresh" :loading="loading">刷新数据</el-button>
      </div>
    </div>

    <!-- 提现概览 -->
    <div class="section-title"><i class="el-icon-wallet"></i> 提现概览</div>
    <el-row :gutter="16" class="stat-row">
      <el-col :span="6" v-for="card in statCards" :key="card.label">
        <div class="stat-card" :class="card.cls">
          <div class="stat-icon"><i :class="card.icon"></i></div>
          <div class="stat-main">
            <div class="stat-label">{{ card.label }}</div>
            <div class="stat-value">{{ card.count }}</div>
          </div>
          <div class="stat-right">
            <div class="stat-amount">{{ card.amount }}</div>
            <div class="stat-sub">{{ card.sub }}</div>
          </div>
        </div>
      </el-col>
    </el-row>

    <!-- 提现趋势 -->
    <div class="section-title"><i class="el-icon-s-data"></i> 提现趋势分析</div>
    <div class="chart-card">
      <div class="chart-header">
        <span class="chart-title"><i class="el-icon-s-data"></i> 提现趋势（近{{ timeType === 0 ? '今日' : timeType === 1 ? '昨日' : timeType === 2 ? '7天' : timeType === 3 ? '30天' : timeType === 4 ? '半年' : '一年' }}）</span>
      </div>
      <div ref="withdrawalTrendChart" class="chart-container"></div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

export default {
  name: 'WithdrawalOverview',
  props: {
    channelId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      timeType: 0,
      loading: false,
      statistics: {
        pendingCount: 0,
        pendingAmount: 0,
        transferredCount: 0,
        transferredAmount: 0,
        rejectedCount: 0,
        rejectedAmount: 0,
        serviceFeeIncome: 0
      },
      withdrawalTrendChart: null,
      trendData: []
    }
  },
  computed: {
    statCards() {
      const fmt = (v) => this.$options.filters.filterMoney(v)
      return [
        { cls: 'pending',  icon: 'el-icon-time',  label: '待审核',    count: this.statistics.pendingCount + '笔',      amount: '¥' + fmt(this.statistics.pendingAmount),      sub: '待处理金额' },
        { cls: 'success',  icon: 'el-icon-check', label: '已转账',    count: this.statistics.transferredCount + '笔',  amount: '¥' + fmt(this.statistics.transferredAmount),  sub: '已到账金额' },
        { cls: 'rejected', icon: 'el-icon-close', label: '已拒绝',    count: this.statistics.rejectedCount + '笔',     amount: '¥' + fmt(this.statistics.rejectedAmount),     sub: '拒绝金额' },
        { cls: 'fee',      icon: 'el-icon-money', label: '手续费收入', count: '-',                                       amount: '¥' + fmt(this.statistics.serviceFeeIncome),   sub: '累计收益' },
      ]
    }
  },
  mounted() {
    this.loadOverview()
    this.loadTrend()
    this.$nextTick(() => {
      this.initWithdrawalTrendChart()
    })
    // 监听窗口大小变化
    this.resizeHandler = this.handleResize.bind(this)
    window.addEventListener('resize', this.resizeHandler)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler)
    if (this.withdrawalTrendChart) {
      this.withdrawalTrendChart.dispose()
    }
  },
  watch: {
    channelId() {
      this.loadOverview()
      this.loadTrend()
    }
  },
  methods: {
    onTimeTypeChange(val) {
      this.timeType = val
      this.loadOverview()
      this.loadTrend()
    },
    /**
     * 获取提现概览统计
     * 使用接口：GET /api/v1/channel/withdrawal/overview
     */
    async loadOverview() {
      this.loading = true
      try {
        const params = { timeType: this.timeType }
        if (this.channelId) params.channelId = this.channelId
        const res = await this.$axios.get('/api/v1/channel/withdrawal/overview', params)
        if (res && res.code === 1 && res.data) {
          this.statistics = {
            pendingCount: res.data.pendingCount || 0,
            pendingAmount: res.data.pendingAmount || 0,
            transferredCount: res.data.transferredCount || 0,
            transferredAmount: res.data.transferredAmount || 0,
            rejectedCount: res.data.rejectedCount || 0,
            rejectedAmount: res.data.rejectedAmount || 0,
            serviceFeeIncome: res.data.serviceFeeIncome || 0
          }
        }
      } catch (error) {
        console.error('获取提现概览统计失败:', error)
      } finally {
        this.loading = false
      }
    },
    /**
     * 获取提现趋势统计
     * 使用接口：GET /api/v1/channel/withdrawal/trend
     */
    async loadTrend() {
      try {
        const params = { timeType: this.timeType }
        if (this.channelId) params.channelId = this.channelId
        const res = await this.$axios.get('/api/v1/channel/withdrawal/trend', params)
        if (res && res.code === 1 && res.data) {
          this.trendData = Array.isArray(res.data) ? res.data : []
          this.$nextTick(() => { this.updateChart() })
        }
      } catch (error) {
        console.error('获取提现趋势统计失败:', error)
      }
    },
    initWithdrawalTrendChart() {
      if (!this.$refs.withdrawalTrendChart) return
      if (this.withdrawalTrendChart) this.withdrawalTrendChart.dispose()
      this.withdrawalTrendChart = echarts.init(this.$refs.withdrawalTrendChart)
      this.updateChart()
    },
    /**
     * 更新图表数据
     * 作者：孙艺
     */
    updateChart() {
      if (!this.withdrawalTrendChart || !this.trendData || this.trendData.length === 0) {
        return
      }
      
      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params) => {
            let result = params[0].name + '<br/>'
            params.forEach(item => {
              if (item.seriesName === '提现笔数') {
                result += `${item.marker}${item.seriesName}: ${item.value}笔<br/>`
              } else {
                const formattedValue = new Intl.NumberFormat("zh-CN", {
                  maximumFractionDigits: 2
                }).format(item.value)
                result += `${item.marker}${item.seriesName}: ¥${formattedValue}<br/>`
              }
            })
            return result
          }
        },
        legend: {
          data: ['提现笔数', '提现金额', '手续费'],
          bottom: 0
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '15%',
          top: '10%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.trendData.map(item => item.date),
          axisLine: { lineStyle: { color: '#E4E7ED' } },
          axisLabel: { color: '#606266' }
        },
        yAxis: [
          {
            type: 'value',
            name: '笔数',
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { lineStyle: { color: '#E4E7ED', type: 'dashed' } },
            axisLabel: { color: '#606266' }
          },
          {
            type: 'value',
            name: '金额(元)',
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { 
              color: '#606266',
              formatter: (value) => {
                return new Intl.NumberFormat("zh-CN", {
                  maximumFractionDigits: 2
                }).format(value)
              }
            }
          }
        ],
        series: [
          {
            name: '提现笔数',
            type: 'bar',
            data: this.trendData.map(item => item.withdrawalCount),
            itemStyle: { color: '#409EFF', borderRadius: [4, 4, 0, 0] },
            barWidth: '30%'
          },
          {
            name: '提现金额',
            type: 'line',
            yAxisIndex: 1,
            data: this.trendData.map(item => item.withdrawalAmount),
            itemStyle: { color: '#E6A23C' },
            lineStyle: { width: 2 },
            symbol: 'circle',
            symbolSize: 6
          },
          {
            name: '手续费',
            type: 'line',
            yAxisIndex: 1,
            data: this.trendData.map(item => item.serviceFee),
            itemStyle: { color: '#67C23A' },
            lineStyle: { width: 2 },
            symbol: 'circle',
            symbolSize: 6
          }
        ]
      }
      this.withdrawalTrendChart.setOption(option)
    },
    /**
     * 窗口大小变化时重新调整图表
     * 作者：孙艺
     */
    handleResize() {
      if (this.withdrawalTrendChart) {
        this.withdrawalTrendChart.resize()
      }
    },
    /**
     * 刷新数据
     * 作者：孙艺
     */
    refresh() {
      this.loadOverview()
      this.loadTrend()
    }
  }
}
</script>

<style lang="less" scoped>
@import '../../channel.less';

.withdrawal-overview {
  padding: 4px 0;
  box-sizing: border-box;

  .filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #fff;
    padding: 16px 20px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
    margin-bottom: 20px;
    .filter-label { font-size: 13px; color: #606266; margin-right: 8px; }
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    i { margin-right: 8px; color: #409EFF; }
  }

  .stat-row { margin-bottom: 20px; }

  .stat-card {
    display: flex;
    align-items: center;
    background: #fff;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    &:hover { transform: translateY(-2px); box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1); }

    .stat-icon {
      width: 44px; height: 44px; border-radius: 10px;
      display: flex; align-items: center; justify-content: center;
      margin-right: 14px; flex-shrink: 0;
      i { font-size: 22px; color: #fff; }
    }

    .stat-main {
      flex: 1; min-width: 0;
      .stat-label { font-size: 12px; color: #909399; margin-bottom: 6px; }
      .stat-value { font-size: 22px; font-weight: 700; color: #303133; white-space: nowrap; }
    }

    .stat-right {
      text-align: right; flex-shrink: 0; padding-left: 12px;
      .stat-amount { font-size: 15px; font-weight: 600; color: #409EFF; white-space: nowrap; margin-bottom: 4px; }
      .stat-sub { font-size: 11px; color: #C0C4CC; }
    }

    &.pending  .stat-icon { background: linear-gradient(135deg, #E6A23C 0%, #F5C78A 100%); }
    &.success  .stat-icon { background: linear-gradient(135deg, #67C23A 0%, #95D475 100%); }
    &.rejected .stat-icon { background: linear-gradient(135deg, #F56C6C 0%, #F89898 100%); }
    &.fee      .stat-icon { background: linear-gradient(135deg, #409EFF 0%, #66B1FF 100%); }
  }

  .chart-card {
    margin-bottom: 20px;
    .chart-container { height: 300px; }
  }
}
</style>

